<script lang="ts">
	import type { ExtractedDocument, Issue, Severity } from '#lib/types';
	import { buildLineMap, issueRanges, splitsForLine } from '#lib/ui/lines';
	import { SEVERITY_UI, severityLabel } from '#lib/ui/labels';
	import { i18n, t } from '#lib/i18n/state.svelte';
	import { issueTextEn } from '#lib/i18n/issues';

	interface Props {
		doc: ExtractedDocument;
		issues: Issue[];
		/** Görüntüleyicide vurgulanacak issue. */
		focusId?: string | null;
	}

	let { doc, issues, focusId = null }: Props = $props();

	const lines = $derived(buildLineMap(doc));
	const ranges = $derived(issueRanges(issues, doc));
	let minSeverity = $state<'critical' | 'warning' | 'info'>('info');

	const severityRank: Record<Severity, number> = { critical: 0, warning: 1, info: 2, ok: 3 };
	const threshold = $derived(severityRank[minSeverity]);

	let scroller: HTMLDivElement;

	$effect(() => {
		if (!focusId) return;
		const el = scroller?.querySelector<HTMLElement>(`[data-focus="${focusId}"]`);
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'center' });
			el.classList.add('flash');
			setTimeout(() => el.classList.remove('flash'), 1600);
		}
	});

	function severityOf(iss: Issue[]): Severity {
		let best: Severity = 'ok';
		for (const i of iss) if (severityRank[i.severity] < severityRank[best]) best = i.severity;
		return best;
	}

	function lineIssues(lineIndex: number): Issue[] {
		const line = lines[lineIndex];
		const set = new Map<string, Issue>();
		for (const part of splitsForLine(line, ranges)) {
			for (const i of part.issues) set.set(i.id, i);
		}
		return [...set.values()];
	}

	const stats = $derived({
		lines: lines.length,
		pages: doc.pageCount,
		annotated: ranges.length
	});

	const metaText = $derived(
		[
			t('viewer.lines', { n: stats.lines }),
			stats.pages ? t('viewer.pages', { n: stats.pages }) : null,
			t('viewer.annotated', { n: stats.annotated })
		]
			.filter(Boolean)
			.join(' · ')
	);

	function issueTitle(issue: Issue): string {
		return (i18n.lang === 'en' ? issueTextEn(issue)?.title : null) ?? issue.title;
	}
</script>

<div class="viewer card">
	<div class="bar">
		<div class="bar-title">
			<span class="eyebrow">{t('viewer.title')}</span>
			<span class="mono muted small">{metaText}</span>
		</div>
		<div class="row filter">
			<span class="small muted">{t('viewer.highlight')}</span>
			<div class="seg" role="group" aria-label={t('viewer.highlight')}>
				{#each ['critical', 'warning', 'info'] as const as level (level)}
					<button
						class="seg-btn"
						class:active={minSeverity === level}
						onclick={() => (minSeverity = level)}
						aria-pressed={minSeverity === level}
					>
						{severityLabel(level)}
					</button>
				{/each}
			</div>
		</div>
	</div>

	<div class="scroller" bind:this={scroller}>
		<ol class="lines">
			{#each lines as line, li (line.index)}
				{@const iss = lineIssues(li)}
				{@const visible = iss.filter((i) => severityRank[i.severity] <= threshold)}
				{@const worst = severityOf(visible)}
				{@const focused = focusId !== null && visible.some((i) => i.id === focusId)}
				<li
					class="line"
					class:has-issue={visible.length > 0}
					class:focus={focused}
					data-focus={visible.some((i) => i.id === focusId) ? focusId : undefined}
					style:--sev={visible.length ? SEVERITY_UI[worst].dot : 'transparent'}
					title={visible.length ? visible.map((i) => issueTitle(i)).join(' · ') : undefined}
				>
					<span class="gutter">{line.page ? t('common.page', { n: line.page }) : ''}</span>
					<span class="text">
						{#each splitsForLine(line, ranges) as part, pi (pi)}
							{#if part.issues.some((i) => severityRank[i.severity] <= threshold)}
								<mark
									class="hl"
									class:hl-critical={part.issues.some((i) => i.severity === 'critical')}
									class:hl-warning={!part.issues.some((i) => i.severity === 'critical') && part.issues.some((i) => i.severity === 'warning')}
									class:hl-info={part.issues.every((i) => i.severity === 'info' || i.severity === 'ok')}
								>{part.text}</mark
								>
							{:else}{part.text}{/if}
						{/each}
					</span>
					{#if visible.length}
						<span class="count" aria-label={t('viewer.countAria', { n: visible.length })}>{visible.length}</span>
					{/if}
				</li>
			{/each}
		</ol>
	</div>
</div>

<style>
	.viewer {
		display: flex;
		flex-direction: column;
		overflow: hidden;
		min-height: 0;
	}
	.bar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		flex-wrap: wrap;
		padding: 0.75rem 0.95rem;
		border-bottom: 1px solid var(--line);
		background: linear-gradient(180deg, #fff, var(--paper));
	}
	.bar-title {
		display: flex;
		flex-direction: column;
		gap: 0.1rem;
	}
	.eyebrow {
		font-size: 0.68rem;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--muted);
	}
	.seg {
		display: inline-flex;
		background: var(--paper-2);
		border: 1px solid var(--line);
		border-radius: 8px;
		padding: 2px;
		gap: 2px;
	}
	.seg-btn {
		border: 0;
		background: transparent;
		font-size: 0.74rem;
		padding: 0.2rem 0.5rem;
		border-radius: 6px;
		cursor: pointer;
		color: var(--ink-2);
	}
	.seg-btn.active {
		background: #fff;
		box-shadow: var(--shadow-sm);
		font-weight: 600;
		color: var(--ink);
	}
	.scroller {
		max-height: 640px;
		overflow: auto;
		scrollbar-width: thin;
	}
	.lines {
		list-style: none;
		margin: 0;
		padding: 0.55rem 0;
		font-family: var(--font-mono);
		font-size: 12px;
		line-height: 1.7;
	}
	.line {
		position: relative;
		display: grid;
		grid-template-columns: 42px 1fr auto;
		gap: 0.6rem;
		padding: 0 0.9rem 0 0.4rem;
		transition: background 0.15s var(--ease);
	}
	.line:hover {
		background: rgb(250 248 244 / 0.9);
	}
	.line.has-issue::before {
		content: '';
		position: absolute;
		left: 2px;
		top: 0.62em;
		width: 5px;
		height: 5px;
		border-radius: 50%;
		background: var(--sev);
	}
	.focus {
		background: var(--accent-soft);
	}
	:global(.flash) {
		animation: flash-bg 1.4s var(--ease);
	}
	@keyframes flash-bg {
		0%,
		100% {
			background: transparent;
		}
		25% {
			background: #ffe9c9;
		}
	}
	.gutter {
		color: var(--muted);
		font-size: 10.5px;
		text-align: right;
		user-select: none;
		padding-top: 1px;
	}
	.text {
		white-space: pre-wrap;
		overflow-wrap: anywhere;
		color: var(--ink-2);
	}
	.hl {
		border-radius: 3px;
		padding: 1px 0;
		color: inherit;
		background: transparent;
	}
	.hl-critical {
		background: #fbe1de;
		box-shadow: inset 0 -2px 0 #b3261e;
	}
	.hl-warning {
		background: #fbefd8;
		box-shadow: inset 0 -2px 0 #b45309;
	}
	.hl-info {
		background: #e6f0f7;
		box-shadow: inset 0 -2px 0 #1f5f8b;
	}
	.count {
		font-size: 10px;
		color: var(--muted);
		align-self: start;
		margin-top: 3px;
	}
</style>
